import React, { useState, useEffect } from 'react';
import Card from '../../config/ChowaCard';
import RomanNumeralText from '../../components/RomanNumeralText';
import { getEngineDataByModelAndExhaust, getUniqueModelsBySeriesName } from '../../api_config';
import { commonStyles } from '../../config/ChowaDashboardLayout';
import { useChowaTheme } from '../../config/chowaThemeContext';
import { Box, Typography, Grid, Button, CircularProgress } from '@mui/material';
import { SERIES_LIST } from '../../model/UnitSeries';
import TimeEngineHoursBarChart from '../../time-monitoring/timeEngineHoursBarChart';
import AllEngineHoursBarChart from '../../time-monitoring/50ALL_timeEngineHoursBarChart';

// 統計カードコンポーネントをインポート
import ModelRegistrationCard from '../../time-monitoring/ModelRegistrationCard';
import TodayOperatingCard from '../../time-monitoring/TodayOperatingCard';

const ChowaMainDashboard = () => {
  const { clearSeriesColor, isDark } = useChowaTheme();

  // ダッシュボードでは常にデフォルトテーマを使用
  useEffect(() => {
    clearSeriesColor();
  }, [clearSeriesColor]);

  // コンポーネントマウント時に各シリーズのモデルを自動取得
  useEffect(() => {
    SERIES_LIST.forEach(series => {
      fetchModelsForSeries(series.key);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 空の依存配列でマウント時のみ実行

  // 選択中シリーズ・モデルとデータ
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [engineData, setEngineData] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // 各シリーズの動的モデルデータ
  const [seriesModels, setSeriesModels] = useState({});
  const [modelsLoading, setModelsLoading] = useState({});

  // 各シリーズのモデルを動的に取得
  const fetchModelsForSeries = async (seriesKey) => {
    if (seriesModels[seriesKey]) return; // 既に取得済み
    
    console.log(`🔍 Fetching models for series: ${seriesKey}`);
    setModelsLoading(prev => ({ ...prev, [seriesKey]: true }));
    try {
      const modelsData = await getUniqueModelsBySeriesName(seriesKey);
      console.log(`📊 Models received for ${seriesKey}:`, modelsData);
      setSeriesModels(prev => ({ ...prev, [seriesKey]: modelsData }));
    } catch (err) {
      console.error(`Error fetching models for ${seriesKey}:`, err);
    } finally {
      setModelsLoading(prev => ({ ...prev, [seriesKey]: false }));
    }
  };

  // シリーズ・モデル選択時のデータ取得
  const handleModelSelect = async (seriesKey, model) => {
    setSelectedSeries(seriesKey);
    setSelectedModel(model);
    setLoading(true);
    try {
      // 新しい形式のデータ取得を使用
      const result = await getEngineDataByModelAndExhaust(seriesKey, model);
      
      if (result.engineData && result.engineData.length > 0) {
        setEngineData(result.engineData);
      } else {
        setEngineData([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setEngineData([]);
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className={commonStyles.layout.container}>
      <div className={commonStyles.layout.content}>
        <div className={commonStyles.layout.section}>

          {/* シリーズ別カード */}
          <Grid container spacing={3} sx={{ mb: 0 }}>
            {SERIES_LIST.map((series) => (
              <Grid item xs={12} md={4} key={series.key}>
                                                  <Card 
                   style={{ 
                     padding: '24px', 
                     borderColor: series.color,
                     borderWidth: '2px',
                     display: 'flex',
                     flexDirection: 'column'
                   }}
                 >
                  <Typography variant="h6" sx={{ 
                    mb: 3, 
                    fontWeight: 600, 
                    color: series.color,
                    textAlign: 'center'
                  }}>
                    {series.name}
                  </Typography>
                  <Box sx={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    paddingBottom: 0
                  }}>
                                         <Grid container spacing={2} justifyContent="flex-start" sx={{ marginBottom: 0 }}>
                       {modelsLoading[series.key] ? (
                         <Grid item xs={12}>
                           <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                             <CircularProgress size={24} sx={{ color: series.color }} />
                           </Box>
                         </Grid>
                       ) : seriesModels[series.key] && seriesModels[series.key].length > 0 ? (
                         seriesModels[series.key].map((model) => (
                           <Grid item key={model}>
                             <Button
                               variant={selectedSeries === series.key && selectedModel === model ? "contained" : "outlined"}
                               onClick={() => handleModelSelect(series.key, model)}
                               sx={{
                                 backgroundColor: selectedSeries === series.key && selectedModel === model ? series.color : 'transparent',
                                 borderColor: series.color,
                                 color: selectedSeries === series.key && selectedModel === model ? 'white' : series.color,
                                 '&:hover': {
                                   backgroundColor: selectedSeries === series.key && selectedModel === model
                                     ? (isDark ? `${series.color}CC !important` : `${series.color}40 !important`)
                                     : (isDark ? `${series.color}30 !important` : `${series.color}25 !important`),
                                   borderColor: `${series.color} !important`
                                 },
                                 minWidth: 120,
                                 height: 48,
                                 fontWeight: 500
                               }}
                             >
                               <RomanNumeralText text={model} />
                             </Button>
                           </Grid>
                         ))
                       ) : (
                         <Grid item xs={12}>
                           <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                             モデルが見つかりません
                           </Typography>
                         </Grid>
                       )}
                     </Grid>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* 選択中シリーズ・モデルのチャート表示 */}
          {selectedSeries && selectedModel && (
            <Box sx={{ mt: 2 }}>
              {/* 統計カード */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <ModelRegistrationCard 
                    totalUnits={engineData.length} 
                    seriesColor={SERIES_LIST.find(s => s.key === selectedSeries)?.color} 
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TodayOperatingCard 
                    operatingUnits={engineData.filter(item => {
                      const today = new Date();
                      const itemDate = new Date(item.timestamp);
                      return itemDate.toDateString() === today.toDateString();
                    }).length} 
                    seriesColor={SERIES_LIST.find(s => s.key === selectedSeries)?.color} 
                  />
                </Grid>
              </Grid>

              {loading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <Typography sx={{ ml: 2 }} color="text.secondary">
                    データを読み込み中...
                  </Typography>
                </Box>
              ) : (
                <>
                  <TimeEngineHoursBarChart 
                    data={engineData} 
                    isDark={isDark}
                    seriesColor={SERIES_LIST.find(s => s.key === selectedSeries)?.color} 
                    selectedModel={selectedModel} 
                  />
                  <div style={{ marginTop: '32px' }}>
                    <AllEngineHoursBarChart 
                      data={engineData} 
                      isDark={isDark}
                      seriesColor={SERIES_LIST.find(s => s.key === selectedSeries)?.color} 
                      selectedModel={selectedModel} 
                    />
                  </div>
                </>
              )}
            </Box>
          )}

        </div>
      </div>
    </div>
  );
};

export default ChowaMainDashboard; 